import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState, useCallback } from 'react';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

import Button from 'core/buttons/Button';
import ReloadButton from 'core/buttons/ReloadButton';
import ExportDocuments from 'core/lab/ExportDocuments';
import TableGridFilter from 'core/table/TableGridFilter';
import ColumnVisibility from 'core/table/ColumnVisibility';
import SelectPartner from 'components/partners/SelectPartner';
import SelectCustomer from 'components/customers/SelectCustomer';
import TableActionsContainer from 'core/table/TableActionsContainer';

import Auth from 'utils/Auth';
import app from 'app/config/app';
import isEmpty from 'helpers/isEmpty';
import { currentRouteName } from 'routes';
import FilterType from 'app/config/FilterType';
import useQueryParams from 'hooks/useQueryParams';
import { getPartners } from 'app/features/partners/partnerSaga';
import {
  getCustomerKycDownloadReport,
  getCustomerKycs,
} from 'app/features/customer-kycs/customerKycSaga';
import { kycMethodOptions, kycStatusOptions, kycTypeOptions } from 'app/config/KYC';
import {
  deleteFilterParams,
  resetCustomerKycReportDownload,
  setParam,
  toggleFilter,
} from 'app/features/customer-kycs/customerKycSlice';
import FilterButton from 'core/buttons/FilterButton';

export default function CustomerKycsActions({
  table,
  enableFilterByPartner,
  defaultFilterQueries,
}) {
  const dispatch = useDispatch();

  const customerKycState = useSelector((state) => state.customerKycs.index);
  const partnerState = useSelector((state) => state.partners.index);
  const { data, params, isLoading, isError, errorCode } = partnerState;

  const partnerOptions = data?.map((item) => ({
    label: item.name,
    value: item.id,
  }));

  const [queryParams, updateQueryParams] = useQueryParams(app.ignoreFilterKeys);

  let availableFilters = [
    {
      label: 'Partner',
      value: 'partner_id',
      type: FilterType.select,
      options: partnerOptions,
    },
    {
      label: 'KYC status',
      value: 'kyc_status',
      type: FilterType.select,
      options: kycStatusOptions,
    },
    {
      label: 'KYC method',
      value: 'kyc_method',
      type: FilterType.select,
      options: kycMethodOptions,
    },
    {
      label: 'KYC type',
      value: 'kyc_type',
      type: FilterType.select,
      options: kycTypeOptions,
    },
  ];

  const { params: appliedFilters, success, isFilterOpen } = customerKycState;
  
  const handleOnFilter = useCallback((data) => {
    dispatch(setParam({ ...data, page_number: 1 }));
    dispatch(toggleFilter());
  }, []);

  const handleOnReset = useCallback((e) => {
    dispatch(deleteFilterParams([...availableFilters.map((filter) => filter.value)]));
  });

  const handlePdfDownloadReport = () => {
    dispatch(
      getCustomerKycDownloadReport({
        format: 'pdf',
        ...appliedFilters,  
      }),
    );
  };

  const handleExcelDownloadReport = () => {
    dispatch(
      getCustomerKycDownloadReport({
        format: 'excel',
        ...appliedFilters,  
      }),
    );
  };

  useEffect(() => {
    if (success) {
      dispatch(resetCustomerKycReportDownload());
    }
  }, [success, dispatch]);

  const partnerId = appliedFilters?.partner_id ?? Auth.getPartnerId();

  useEffect(() => {
    // update query parameters only for index page
    if (currentRouteName() === 'customer-kycs.index') {
      updateQueryParams(appliedFilters);
    }
  }, [JSON.stringify(appliedFilters)]);

  useEffect(() => {
    dispatch(setParam(queryParams));
  }, [JSON.stringify(queryParams)]);

  useEffect(() => {
    dispatch(getPartners({ ...params, page_size: 100 }));
  }, []);

  return (
    <>
      <Box>
        <TableGridFilter
          showFilter={isFilterOpen}
          appliedFilters={appliedFilters}
          availableFilters={availableFilters}
          onFilter={handleOnFilter}
          onReset={handleOnReset}
        />
      </Box>
      <TableActionsContainer>
        <Box display="flex" gap={2}>
          {!isEmpty(partnerId) && (
            <SelectCustomer
              id="select-customer"
              onSelected={(value) => {
                if (!isEmpty(value)) {
                  dispatch(setParam({ user_id: value, page_number: 1 }));
                } else {
                  dispatch(deleteFilterParams('user_id'));
                }
              }}
              sx={{ width: '300px' }}
              partnerId={partnerId}
            />
          )}
        </Box>
        <Box display="flex" gap={2}>
          {table && <ColumnVisibility table={table} />}
          <ReloadButton
            onClick={() => {
              dispatch(
                getCustomerKycs({ ...appliedFilters, page_number: 1, ...defaultFilterQueries }),
              );
            }}
          />
          {/* <MoreActions
            onExportToCsv={() => Sheet.exportReactTableSelectedRowsToCsv(table, 'customer_kycs_')}
            onExportToExcel={() =>
              Sheet.exportReactTableSelectedRowsToExcel(table, 'customer_kycs_')
            }
          /> */}

          <ExportDocuments
            onExcelDownload={handleExcelDownloadReport}
            onPdfDownload={handlePdfDownloadReport}
          />
        </Box>
      </TableActionsContainer>
    </>
  );
}

CustomerKycsActions.propTypes = {
  table: PropTypes.object,
  enableFilterByPartner: PropTypes.bool,
  defaultFilterQueries: PropTypes.object,
};

CustomerKycsActions.defaultProps = {
  enableFilterByPartner: true,
};
